import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminNotifications = ({ user, isTenantSuspended = false }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ne pas faire d'appels si le tenant est suspendu
    if (isTenantSuspended) {
      setLoading(false);
      return;
    }

    if (user.role === 'ADMIN' || user.role === 'PRINCIPAL') {
      fetchNotifications();
    }
  }, [user.role, isTenantSuspended]);

  const fetchNotifications = async () => {
    // Ne pas faire d'appels si le tenant est suspendu
    if (isTenantSuspended) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.slice(0, 5)); // Afficher seulement les 5 dernières
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Ne pas afficher de toast si le tenant est suspendu
      if (!isTenantSuspended) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les notifications"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(notifications.map(notif => 
        notif.id === notificationId 
          ? { ...notif, status: 'READ' }
          : notif
      ));
      
      toast({
        title: "Notification marquée comme lue",
        description: "La notification a été marquée comme lue avec succès"
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Erreur lors de la mise à jour de la notification"
      });
    }
  };

  // Ne pas afficher pour les autres rôles
  if (user.role !== 'ADMIN' && user.role !== 'PRINCIPAL') {
    return null;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Bell className="w-5 h-5 mr-2 text-blue-600" />
            Notifications
          </div>
          {notifications.filter(n => n.status === 'UNREAD').length > 0 && (
            <Badge variant="destructive">
              {notifications.filter(n => n.status === 'UNREAD').length}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>Messages du Super Admin et notifications système</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4 text-gray-500">
            Chargement des notifications...
          </div>
        ) : notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div 
                key={notification.id} 
                className={`p-3 rounded-lg border ${
                  notification.status === 'UNREAD' 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-sm">{notification.title}</h4>
                      {notification.status === 'UNREAD' && (
                        <AlertCircle className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    <div className="text-xs text-gray-500 mt-2">
                      {notification.sender_name && `De: ${notification.sender_name} • `}
                      {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  {notification.status === 'UNREAD' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => markAsRead(notification.id)}
                      className="ml-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {notifications.length === 5 && (
              <div className="text-center pt-2">
                <Button variant="ghost" size="sm" disabled>
                  Voir plus de notifications (à venir)
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>Aucune notification pour le moment</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminNotifications;